import { useVideos } from "../../hooks/useVideos";
import { useVideoFilters } from "../../hooks/useVideoSearch";
import VideoItem from "./VideoItem";
import TagFilter from "../search/TagFilter";
import { AuthService } from "../../services/auth";
import FilterResults from "../search/FilterResults";
import VideoGridSkeleton from "../skeletons/VideoGridSkeleton";
import { AuthContextProps } from "react-oidc-context";
import { useI18n } from "../../i18n";

type VideoListProps = {
  onWatchVideo: (videoUrl: string, videoKey: string) => void;
  auth: AuthContextProps;
};

function VideoList({ onWatchVideo, auth }: VideoListProps) {
  const { t } = useI18n();
  const { videos, isLoading, error } = useVideos();

  const isAdmin = AuthService.isAdmin(auth);

  const {
    tagFilters,
    filterResult,
    toggleTagValue,
    clearAllFilters,
    clearTagKeyFilters,
    hasActiveFilters,
    availableTags,
  } = useVideoFilters({ videos });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex gap-2">
          <div className="skeleton h-10 w-24"></div>
          <div className="skeleton h-10 w-32"></div>
          <div className="skeleton h-10 w-20"></div>
        </div>
        <VideoGridSkeleton count={12} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        <div>
          <h3 className="font-bold">{t("videoList.errorLoading")}</h3>
          <div className="text-sm">{error.message}</div>
        </div>
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="hero min-h-96 bg-base-200 rounded-box">
        <div className="hero-content text-center">
          <div>
            <h3 className="text-2xl font-bold mb-2">
              {t("videoList.noVideos")}
            </h3>
            <p className="opacity-70">{t("videoList.noVideosBody")}</p>
          </div>
        </div>
      </div>
    );
  }

  const displayVideos = filterResult.videos;

  return (
    <div className="space-y-6">
      {isAdmin && (
        <>
          <TagFilter
            availableTags={availableTags}
            selectedFilters={tagFilters}
            onToggleTag={toggleTagValue}
            onClearTagKey={clearTagKeyFilters}
            onClearAll={clearAllFilters}
          />

          <FilterResults
            filterResult={filterResult}
            hasActiveFilters={hasActiveFilters}
          />
        </>
      )}

      {displayVideos.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-6">
          {displayVideos.map((video) => (
            <VideoItem
              key={video.key}
              title={video.key}
              tags={video.tags}
              dates={video.dates}
              onWatchVideo={onWatchVideo}
              auth={auth}
            />
          ))}
        </div>
      ) : hasActiveFilters ? null : (
        <div className="hero min-h-64 bg-base-200 rounded-box">
          <div className="hero-content text-center">
            <div>
              <h3 className="text-xl font-bold mb-2">
                {t("videoList.noFound")}
              </h3>
              <p className="opacity-70">{t("videoList.noFoundBody")}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VideoList;
